import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Telescope, Loader2, TrendingUp, TrendingDown, MinusCircle, Zap, AlertTriangle,
  Radio, Calendar, Sparkles, ArrowRight,
} from "lucide-react";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { api } from "@/lib/api";

const scoreColor = (v) => v >= 75 ? "#10b981" : v >= 50 ? "#3b82f6" : v >= 30 ? "#f59e0b" : "#ef4444";
const newsRiskColor = (level) =>
  level === "high" ? "#ef4444" : level === "medium" ? "#f59e0b" : level === "low" ? "#10b981" : "#6b7280";

const RadialGauge = ({ value, color, size = 68 }) => {
  const data = [{ name: "score", value: value ?? 0, fill: color || "#3b82f6" }];
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="60%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background={{ fill: "#1f2937" }} dataKey="value" cornerRadius={4} />
        </RadialBarChart>
      </ResponsiveContainer>
    </div>
  );
};

const StatCell = ({ label, value, color, Icon, suffix = "%", small = false }) => (
  <div className={`flex items-center gap-3 ${small ? "" : "flex-1"}`}>
    <div className="relative">
      <RadialGauge value={value} color={color} size={small ? 46 : 68} />
      {Icon && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Icon className="w-3 h-3" style={{ color }} />
        </div>
      )}
    </div>
    <div className="min-w-0">
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">{label}</div>
      <div className={`${small ? "text-xl" : "text-2xl"} font-heading font-black leading-none`} style={{ color }}>
        {value === null || value === undefined ? "—" : `${Math.round(value)}${suffix}`}
      </div>
    </div>
  </div>
);

const NewsRiskCell = ({ risk, small = false }) => {
  const color = newsRiskColor(risk?.level);
  const label = (risk?.level || "unknown").toUpperCase();
  return (
    <div className={`flex items-center gap-3 ${small ? "" : "flex-1"}`}>
      <div className={`${small ? "w-11 h-11 text-xs" : "w-16 h-16 text-sm"} rounded-full border-2 flex flex-col items-center justify-center font-heading font-black leading-none`}
           style={{ borderColor: color, color, background: `${color}10` }}>
        <AlertTriangle className={`${small ? "w-3 h-3" : "w-4 h-4"} mb-0.5`} />
        <span className="text-[8px] font-mono tracking-widest">{label}</span>
      </div>
      <div className="min-w-0">
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">News Risk</div>
        <div className={`${small ? "text-xs" : "text-sm"} font-mono`} style={{ color }}>
          {risk?.count_next_24h || 0} events · {risk?.high_impact_count || 0} high-impact
        </div>
      </div>
    </div>
  );
};

const SymbolCard = ({ p }) => {
  if (p.status !== "ok") {
    return (
      <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-4" data-testid={`prediction-card-${p.symbol}`}>
        <div className="flex items-center gap-2 mb-1">
          <div className="font-mono font-bold text-base">{p.symbol}</div>
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#6b7280]/20 text-[#9ca3af]">
            NO DATA
          </span>
        </div>
        <div className="text-xs text-[#9ca3af]">{p.narrative || "Insufficient bars — awaiting data."}</div>
      </div>
    );
  }
  const trendColor = scoreColor(p.trend_prob);
  const rangeColor = scoreColor(p.range_prob);
  const volColor = scoreColor(p.high_volatility_prob);
  const confColor = scoreColor(p.confidence);
  const nextEvent = p.news_risk?.next_event;

  return (
    <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-5 space-y-4 hover:border-[#3b82f6]/40 transition-colors"
         data-testid={`prediction-card-${p.symbol}`}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-mono font-black text-xl">{p.symbol}</div>
            {p.trend_direction && p.trend_prob >= 60 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${
                p.trend_direction === "bullish"
                  ? "text-[#10b981] bg-[#10b981]/10 border border-[#10b981]/40"
                  : "text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/40"
              }`}>
                {p.trend_direction === "bullish"
                  ? <TrendingUp className="w-3 h-3" />
                  : <TrendingDown className="w-3 h-3" />}
                {p.trend_direction}
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono text-[#6b7280] mt-0.5">
            {p.timeframe} · {p.context?.session || "—"} · ATR pct {((p.context?.atr_percentile || 0) * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {/* 5 gauges */}
      <div className="grid grid-cols-2 gap-4">
        <StatCell label="Trend"      value={p.trend_prob}          color={trendColor} Icon={TrendingUp} />
        <StatCell label="Range"      value={p.range_prob}          color={rangeColor} Icon={MinusCircle} />
        <StatCell label="Volatility" value={p.high_volatility_prob} color={volColor}   Icon={Zap} />
        <StatCell label="Confidence" value={p.confidence}          color={confColor}  Icon={Sparkles} />
      </div>

      {/* News risk row */}
      <div className="border-t border-[#1f2937] pt-3">
        <NewsRiskCell risk={p.news_risk} small />
        {nextEvent && (
          <div className="mt-2 flex items-center gap-2 text-[10px] font-mono text-[#9ca3af]">
            <Calendar className="w-3 h-3 text-[#f59e0b]" />
            <span className="truncate">{nextEvent.event_name}</span>
            <span className="text-[#6b7280]">·</span>
            <span>{nextEvent.currency}</span>
            {nextEvent.scheduled_at && (
              <>
                <span className="text-[#6b7280]">·</span>
                <span>{nextEvent.scheduled_at.slice(5, 16).replace("T", " ")} UTC</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Narrative */}
      <div className="text-xs text-[#9ca3af] leading-relaxed border-l-2 border-[#3b82f6]/30 pl-3 italic">
        {p.narrative}
      </div>
    </div>
  );
};

const PortfolioSummary = ({ summary }) => {
  if (!summary) return null;
  const items = [
    { label: "High-Conviction Trends", value: summary.high_conviction_trends, color: "#10b981", Icon: TrendingUp },
    { label: "Range-Bound",            value: summary.range_bound,             color: "#3b82f6", Icon: MinusCircle },
    { label: "High Volatility",        value: summary.high_volatility,         color: "#f59e0b", Icon: Zap },
    { label: "News Alerts",            value: summary.news_alerts,             color: "#ef4444", Icon: AlertTriangle },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="prediction-summary">
      {items.map((it) => {
        const list = it.value || [];
        return (
          <div key={it.label}
               className="rounded-lg border p-4"
               style={{ borderColor: `${it.color}40`, background: `${it.color}08` }}>
            <div className="flex items-center gap-2 mb-2">
              <it.Icon className="w-4 h-4" style={{ color: it.color }} />
              <div className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: it.color }}>
                {it.label}
              </div>
            </div>
            <div className="font-heading text-3xl font-black mb-1" style={{ color: it.color }}>
              {list.length}
            </div>
            <div className="text-[10px] font-mono text-[#9ca3af] truncate">
              {list.length === 0 ? "None" : list.slice(0, 5).join(" · ")}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Prediction() {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [timeframe, setTimeframe] = useState("H1");

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get(`/prediction/today?timeframe=${timeframe}`),
        api.get(`/prediction/accuracy?days=30`),
      ]);
      setPayload(r.data);
      setAccuracy(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load predictions");
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]" data-testid="prediction-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  const summary = payload?.portfolio_summary;
  const preds = payload?.predictions || [];
  const avgConf = summary?.avg_confidence ?? 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="prediction-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="vx-label mb-1 flex items-center gap-2">
            <Telescope className="w-3 h-3" /> AI Market Prediction Dashboard
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">
            Today&apos;s Forecast
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl leading-relaxed">
            Top-of-day probabilities per symbol — will it trend, range, or blow out on volatility?
            Where&apos;s the news risk sitting? All rolled up into one dashboard so you know what to expect before the session opens.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Avg Confidence</div>
            <div className="font-heading text-3xl font-black" style={{ color: scoreColor(avgConf) }}>
              {Math.round(avgConf)}%
            </div>
          </div>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="bg-[#111827] border border-[#1f2937] rounded px-3 py-2 text-xs font-mono text-[#e5e7eb] focus:border-[#3b82f6] focus:outline-none"
            data-testid="prediction-tf-select"
          >
            {["M15", "M30", "H1", "H4", "D1"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
          </select>
          <button
            onClick={load}
            className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af] hover:text-[#3b82f6] transition-colors"
            data-testid="prediction-refresh-btn"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Portfolio summary */}
      <PortfolioSummary summary={summary} />

      {/* 30-day accuracy backtest */}
      {accuracy && accuracy.total_evaluated > 0 && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-5" data-testid="prediction-accuracy-card">
          <div className="flex items-center justify-between mb-4">
            <div className="vx-label">30-Day Backtest — Real Outcomes</div>
            <div className="text-[10px] font-mono text-[#6b7280]">
              {accuracy.total_evaluated} evaluated predictions
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "trend",           label: "Trend Calls",       obj: accuracy.trend,           color: "#10b981" },
              { key: "range",           label: "Range Calls",       obj: accuracy.range,           color: "#3b82f6" },
              { key: "high_volatility", label: "High-Vol Calls",    obj: accuracy.high_volatility, color: "#f59e0b" },
              { key: "news",            label: "News-Risk Calls",   obj: accuracy.news,            color: "#a855f7" },
            ].map((m) => {
              const acc = m.obj?.accuracy_pct;
              return (
                <div key={m.key} className="text-center" data-testid={`accuracy-${m.key}`}>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">{m.label}</div>
                  <div className="font-heading text-3xl font-black mt-1" style={{ color: acc !== null ? m.color : "#6b7280" }}>
                    {acc !== null && acc !== undefined ? `${acc}%` : "—"}
                  </div>
                  <div className="text-[9px] font-mono text-[#6b7280] mt-0.5">
                    {m.obj?.correct || 0} / {m.obj?.total || 0}
                  </div>
                </div>
              );
            })}
          </div>
          {accuracy.avg_captured_confidence !== null && (
            <div className="mt-4 pt-3 border-t border-[#1f2937] text-[10px] font-mono text-[#9ca3af]">
              Avg captured confidence at prediction time: <span className="text-[#e5e7eb] font-bold">{accuracy.avg_captured_confidence}%</span>
              {" · "}
              <span className="italic">
                Snapshots are captured every time you load this page and evaluated 24h later against actual price movement.
              </span>
            </div>
          )}
        </div>
      )}
      {accuracy && accuracy.total_evaluated === 0 && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-4 text-xs text-[#9ca3af]" data-testid="prediction-accuracy-empty">
          Backtest history is building — predictions captured today will be evaluated in 24h against actual price movement.
          No fake data; the accuracy stats appear here once real snapshots are ready.
        </div>
      )}

      {/* Per-symbol cards */}
      <div>
        <div className="vx-label mb-3 flex items-center gap-2">
          <Radio className="w-3 h-3" /> Per-Symbol Forecasts · {timeframe}
        </div>
        {preds.length === 0 ? (
          <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-8 text-center text-sm text-[#9ca3af]">
            No symbol data yet — install the EA on a chart to begin.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {preds.map(p => <SymbolCard key={p.symbol} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
